import { useState, useEffect, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { fetchAnalyticsFilters } from "@/features/analytics/api";
import type { AnalyticsFiltersOptions } from "@/features/analytics/types";

interface PanelFiltersProps {
  filters: {
    clienteFinalId: number | null;
    programaId: number | null;
    actividadId: number | null;
    dateFrom: string | null;
    dateTo: string | null;
  };
  onFiltersChange: (filters: {
    clienteFinalId: number | null;
    programaId: number | null;
    actividadId: number | null;
    dateFrom: string | null;
    dateTo: string | null;
  }) => void;
  clienteIntermedioId: number | null;
}

const toInputDate = (value: string | null): string => value ?? "";
const fromInputDate = (value: string) => (value ? value : null);

export function PanelFilters({
  filters,
  onFiltersChange,
  clienteIntermedioId,
}: PanelFiltersProps) {
  const [options, setOptions] = useState<AnalyticsFiltersOptions | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchAnalyticsFilters()
      .then((data) => {
        if (!active) return;
        setOptions(data);
        setError(null);
      })
      .catch((err) => {
        console.error("Error cargando filtros analytics", err);
        if (!active) return;
        setError("No se pudieron cargar los filtros");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const derivedOptions = useMemo(() => {
    if (!options) {
      return { clientesFinales: [], programas: [], actividades: [] };
    }
    // Filtrar clientes finales por cliente intermedio
    const finales = options.clientesFinales.filter((cf) =>
      clienteIntermedioId
        ? cf.clienteIntermedioId === clienteIntermedioId
        : true,
    );
    // Filtrar programas por cliente final seleccionado
    const programas = options.programas.filter((programa) =>
      filters.clienteFinalId
        ? programa.clienteFinalId === filters.clienteFinalId
        : true,
    );
    // Filtrar actividades por programa seleccionado
    const actividades = options.actividades.filter((actividad) =>
      filters.programaId ? actividad.programaId === filters.programaId : true,
    );
    return { clientesFinales: finales, programas, actividades };
  }, [
    options,
    clienteIntermedioId,
    filters.clienteFinalId,
    filters.programaId,
  ]);

  const handleClienteChange = (id: number | null) => {
    onFiltersChange({
      ...filters,
      clienteFinalId: id,
      programaId: null, // Reset programa cuando cambia cliente
      actividadId: null, // Reset actividad cuando cambia cliente
    });
  };

  const handleProgramaChange = (id: number | null) => {
    onFiltersChange({
      ...filters,
      programaId: id,
      actividadId: null, // Reset actividad cuando cambia programa
    });
  };

  const handleActividadChange = (id: number | null) => {
    onFiltersChange({
      ...filters,
      actividadId: id,
    });
  };

  const handleDateFromChange = (value: string) => {
    onFiltersChange({
      ...filters,
      dateFrom: fromInputDate(value),
    });
  };

  const handleDateToChange = (value: string) => {
    onFiltersChange({
      ...filters,
      dateTo: fromInputDate(value),
    });
  };

  const resetFilters = () => {
    onFiltersChange({
      clienteFinalId: null,
      programaId: null,
      actividadId: null,
      dateFrom: null,
      dateTo: null,
    });
  };

  const filtersDisabled = loading || !!error;

  return (
    <Card>
      <CardContent>
        <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end">
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">
                Cliente Final
              </span>
              <Select
                value={
                  filters.clienteFinalId
                    ? String(filters.clienteFinalId)
                    : "all"
                }
                onValueChange={(next) =>
                  handleClienteChange(next === "all" ? null : Number(next))
                }
                disabled={
                  filtersDisabled || derivedOptions.clientesFinales.length === 0
                }
              >
                <SelectTrigger className="min-w-[180px]">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {derivedOptions.clientesFinales.map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {item.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">
                Programa
              </span>
              <Select
                value={filters.programaId ? String(filters.programaId) : "all"}
                onValueChange={(next) =>
                  handleProgramaChange(next === "all" ? null : Number(next))
                }
                disabled={
                  filtersDisabled || derivedOptions.programas.length === 0
                }
              >
                <SelectTrigger className="min-w-[180px]">
                  <SelectValue placeholder="Selecciona un programa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {derivedOptions.programas.map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {item.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">
                Actividad
              </span>
              <Select
                value={
                  filters.actividadId ? String(filters.actividadId) : "all"
                }
                onValueChange={(next) =>
                  handleActividadChange(next === "all" ? null : Number(next))
                }
                disabled={
                  filtersDisabled || derivedOptions.actividades.length === 0
                }
              >
                <SelectTrigger className="min-w-[180px]">
                  <SelectValue placeholder="Selecciona una actividad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {derivedOptions.actividades.map((item) => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {item.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 mt-4 lg:mt-0">
            <div className="break flex flex-col gap-1 ">
              <span className="text-xs font-medium text-muted-foreground">
                Fecha desde
              </span>
              <Input
                type="date"
                value={toInputDate(filters.dateFrom)}
                onChange={(e) => handleDateFromChange(e.target.value)}
                className="min-w-[170px]"
                disabled={filtersDisabled}
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">
                Fecha hasta
              </span>
              <Input
                type="date"
                value={toInputDate(filters.dateTo)}
                onChange={(e) => handleDateToChange(e.target.value)}
                className="min-w-[170px]"
                disabled={filtersDisabled}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={resetFilters}
                disabled={filtersDisabled}
              >
                Limpiar filtros
              </Button>
              {error && <span className="text-sm text-red-600">{error}</span>}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
